// GlobalSearch component
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  Users,
  Building2,
  DollarSign,
  Settings,
  MessageSquare,
  Home,
} from 'lucide-react';
import { removeAccents } from '@/lib/formatters';

import { Button } from '@/components/ui/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useRBAC } from '@/components/ui/RBAC';
import { supabase } from '@/integrations/supabase/client';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  href: string;
  icon: React.ElementType;
  category: string;
  keywords?: string[];
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { hasAccess } = useRBAC();

  // Estados para resultados dinâmicos
  const [isLoading, setIsLoading] = useState(false);
  const [assocResults, setAssocResults] = useState<
    { id: string; nome: string; email?: string | null }[]
  >([]);
  const [empResults, setEmpResults] = useState<
    { id: string; nome_fantasia?: string; razao_social?: string; cnpj?: string }[]
  >([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Listener para abrir a busca via evento customizado
  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener('global-search-open', openHandler as EventListener);
    return () => window.removeEventListener('global-search-open', openHandler as EventListener);
  }, []);

  // Verificar permissões apenas uma vez
  const canViewAssociados = useMemo(
    () => hasAccess(['presidencia', 'diretoria', 'juridico', 'homologacao']),
    [hasAccess]
  );

  const canViewEmpresas = useMemo(
    () => hasAccess(['presidencia', 'diretoria', 'juridico', 'homologacao']),
    [hasAccess]
  );

  const allResults: SearchResult[] = [
    // Geral / Dashboard
    {
      id: 'dashboard',
      title: 'Dashboard',
      description: 'Visão geral do sistema',
      href: '/dashboard',
      icon: FileText,
      category: 'Geral',
      keywords: ['inicio', 'home', 'principal', 'visão geral'],
    },

    // Associados
    {
      id: 'associados',
      title: 'Associados',
      description: 'Listagem e consulta de associados',
      href: '/associados',
      icon: Users,
      category: 'Associados',
      keywords: ['consulta', 'listagem', 'pessoas', 'membros', 'sócio'],
    },

    // Empresas
    {
      id: 'empresas',
      title: 'Empresas',
      description: 'Listagem e consulta de empresas',
      href: '/empresas',
      icon: Building2,
      category: 'Empresas',
      keywords: ['consulta', 'listagem', 'cnpj', 'organizações'],
    },

    // Financeiro
    {
      id: 'financeiro',
      title: 'Financeiro - Visão Geral',
      description: 'Painel financeiro',
      href: '/financeiro',
      icon: DollarSign,
      category: 'Financeiro',
      keywords: ['financeiro', 'pagamentos', 'recebimentos'],
    },
    {
      id: 'financeiro-receber',
      title: 'Contas a Receber',
      description: 'Receitas e cobranças',
      href: '/financeiro/contas-receber',
      icon: DollarSign,
      category: 'Financeiro',
      keywords: ['receber', 'receitas', 'cobrança'],
    },
    {
      id: 'financeiro-pagar',
      title: 'Contas a Pagar',
      description: 'Despesas e pagamentos',
      href: '/financeiro/contas-pagar',
      icon: DollarSign,
      category: 'Financeiro',
      keywords: ['pagar', 'despesas', 'boletos'],
    },
    {
      id: 'financeiro-inadimplencia',
      title: 'Inadimplência',
      description: 'Gestão de inadimplentes',
      href: '/financeiro/inadimplencia',
      icon: DollarSign,
      category: 'Financeiro',
      keywords: ['inadimplencia', 'atraso', 'cobrança'],
    },
    {
      id: 'financeiro-fluxo',
      title: 'Fluxo de Caixa',
      description: 'Entradas e saídas',
      href: '/financeiro/fluxo-caixa',
      icon: DollarSign,
      category: 'Financeiro',
      keywords: ['fluxo', 'caixa', 'cash flow'],
    },
    {
      id: 'financeiro-relatorios',
      title: 'Relatórios Financeiros',
      description: 'Relatórios e análises',
      href: '/financeiro/relatorios',
      icon: FileText,
      category: 'Financeiro',
      keywords: ['relatório', 'analise', 'exportação'],
    },

    // Comunicação
    {
      id: 'comunicacao',
      title: 'Comunicação',
      description: 'Central de comunicação',
      href: '/comunicacao',
      icon: MessageSquare,
      category: 'Comunicação',
      keywords: ['comunicação', 'comunicacao'],
    },
    {
      id: 'comunicacao-mensagens',
      title: 'Mensagens',
      description: 'Envio e histórico de mensagens',
      href: '/comunicacao/mensagens',
      icon: MessageSquare,
      category: 'Comunicação',
      keywords: ['mensagens', 'chat', 'enviar'],
    },

    // Administração
    {
      id: 'administracao',
      title: 'Administração',
      description: 'Configurações do sistema',
      href: '/administracao',
      icon: Settings,
      category: 'Administração',
      keywords: ['admin', 'config', 'painel'],
    },
    {
      id: 'administracao-usuarios',
      title: 'Usuários',
      description: 'Gestão de usuários e perfis',
      href: '/administracao/usuarios',
      icon: Settings,
      category: 'Administração',
      keywords: ['usuários', 'acessos', 'perfis', 'rbac'],
    },
    {
      id: 'administracao-configuracoes',
      title: 'Configurações',
      description: 'Preferências e integrações',
      href: '/administracao/configuracoes',
      icon: Settings,
      category: 'Administração',
      keywords: ['configurações', 'preferências', 'integrações'],
    },
    {
      id: 'administracao-logs',
      title: 'Logs',
      description: 'Registros e auditoria',
      href: '/administracao/logs',
      icon: FileText,
      category: 'Administração',
      keywords: ['logs', 'auditoria', 'registros'],
    },

    // Colônias
    {
      id: 'colonias-dashboard',
      title: 'Colônias - Dashboard',
      description: 'Visão geral das colônias',
      href: '/colonias/dashboard',
      icon: Home,
      category: 'Colônias',
      keywords: ['colônias', 'colonias', 'hotel', 'hospedagem'],
    },
  ];

  const normalize = (text: string) => removeAccents(text.toLowerCase());

  // Função de busca otimizada
  const searchData = useCallback(
    async (searchTerm: string) => {
      if (searchTerm.length < 2) {
        setAssocResults([]);
        setEmpResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMsg(null);

      try {
        const promises: Promise<any>[] = [];

        if (canViewAssociados) {
          promises.push(
            Promise.resolve(
              supabase
                .from('associados')
                .select('id, nome, email')
                .or(`nome.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
                .limit(8)
            ).then((result) => ({ ...result, type: 'associados' }))
          );
        }

        if (canViewEmpresas) {
          promises.push(
            Promise.resolve(
              supabase
                .from('empresas')
                .select('id, nome_fantasia, razao_social, cnpj')
                .or(
                  `nome_fantasia.ilike.%${searchTerm}%,razao_social.ilike.%${searchTerm}%,cnpj.ilike.%${searchTerm}%`
                )
                .limit(8)
            ).then((result) => ({ ...result, type: 'empresas' }))
          );
        }

        const results = await Promise.all(promises);

        // Process results based on type
        results.forEach((result) => {
          if (result.type === 'associados') {
            if (result.error) {
              console.error('Erro ao buscar associados:', result.error);
              setAssocResults([]);
            } else {
              setAssocResults(result.data || []);
            }
          } else if (result.type === 'empresas') {
            if (result.error) {
              console.error('Erro ao buscar empresas:', result.error);
              setEmpResults([]);
            } else {
              setEmpResults(result.data || []);
            }
          }
        });
      } catch (err: any) {
        setErrorMsg(err?.message || 'Erro ao buscar dados.');
        console.error('Erro na busca:', err);
        setAssocResults([]);
        setEmpResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [canViewAssociados, canViewEmpresas]
  );

  // Busca com debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchData(query.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, searchData]);

  // Filtrar resultados estáticos
  const searchResults: SearchResult[] = useMemo(() => {
    const allowed = hasAccess([
      'presidencia',
      'diretoria',
      'juridico',
      'financeiro',
      'atendimento',
      'homologacao',
    ]);
    if (!allowed) return [];

    if (!query.trim()) return allResults;

    const tokens = normalize(query).split(/\s+/).filter(Boolean);

    return allResults.filter((result) => {
      const haystack = [result.title, result.description, result.href, ...(result.keywords || [])]
        .map((s) => normalize(String(s)))
        .join(' ');

      return tokens.every((t) => haystack.includes(t));
    });
  }, [query, hasAccess, allResults]);

  const categories = useMemo(
    () => Array.from(new Set(searchResults.map((r) => r.category))),
    [searchResults]
  );

  const handleItemSelect = (href: string) => {
    navigate(href);
    setOpen(false);
    setQuery('');
  };

  // Limpar busca quando fechar
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setQuery('');
      setAssocResults([]);
      setEmpResults([]);
      setErrorMsg(null);
      setIsLoading(false);
    }
  };

  // Calcular se há resultados
  const hasResults = assocResults.length > 0 || empResults.length > 0 || searchResults.length > 0;
  const showEmpty = !isLoading && !hasResults && query.length >= 2;

  return (
    <>
      <Button
        variant="outline"
        className="relative h-9 w-9 p-0 xl:h-10 xl:w-60 xl:justify-start xl:px-3 xl:py-2"
        onClick={() => setOpen(true)}
      >
        <Search className="h-4 w-4 xl:mr-2" />
        <span className="hidden xl:inline-flex">Buscar...</span>
        <span className="sr-only">Buscar</span>
        <kbd className="pointer-events-none absolute right-1.5 top-2 hidden h-6 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 xl:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={handleOpenChange}>
        <CommandInput
          placeholder="Buscar em todo o sistema..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {showEmpty && (
            <CommandEmpty>
              {errorMsg ? `Erro: ${errorMsg}` : 'Nenhum resultado encontrado.'}
            </CommandEmpty>
          )}

          {isLoading && <CommandEmpty>Buscando...</CommandEmpty>}

          {/* Registros dinâmicos - Associados */}
          {assocResults.length > 0 && (
            <>
              <CommandGroup heading="Associados (Registros)">
                {assocResults.map((a) => (
                  <CommandItem
                    key={`assoc-${a.id}`}
                    value={`assoc-${a.id}-${a.nome}`}
                    onSelect={() => handleItemSelect(`/associados/editar/${a.id}`)}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    <div>
                      <p className="font-medium">{a.nome}</p>
                      <p className="text-sm text-muted-foreground">{a.email || 'Sem email'}</p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Registros dinâmicos - Empresas */}
          {empResults.length > 0 && (
            <>
              <CommandGroup heading="Empresas (Registros)">
                {empResults.map((e) => (
                  <CommandItem
                    key={`emp-${e.id}`}
                    value={`emp-${e.id}-${e.nome_fantasia || e.razao_social}`}
                    onSelect={() => handleItemSelect(`/empresas/editar/${e.id}`)}
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <div>
                      <p className="font-medium">
                        {e.nome_fantasia || e.razao_social || 'Empresa'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {e.razao_social && e.nome_fantasia !== e.razao_social ? e.razao_social : ''}
                        {e.cnpj ? `${e.razao_social ? ' • ' : ''}CNPJ: ${e.cnpj}` : ''}
                      </p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Rotas/Páginas estáticas */}
          {categories.map((category) => (
            <React.Fragment key={category}>
              <CommandGroup heading={category}>
                {searchResults
                  .filter((r) => r.category === category)
                  .map((r) => {
                    const Icon = r.icon;
                    return (
                      <CommandItem
                        key={r.id}
                        value={r.id}
                        onSelect={() => handleItemSelect(r.href)}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        <div>
                          <p className="font-medium">{r.title}</p>
                          <p className="text-sm text-muted-foreground">{r.description}</p>
                        </div>
                      </CommandItem>
                    );
                  })}
              </CommandGroup>
              <CommandSeparator />
            </React.Fragment>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
